import { Component, OnInit, ViewChild } from '@angular/core';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	Validators,
} from '@angular/forms';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services';
import { OptionRadio } from '@shared/interfaces/public-radio-group';
import {
	FormularioRelatorio,
	PeriodosLabel,
	Variavel,
} from '@home/submodulos/dados-meteorologicos/interfaces';
import { Select } from '@layout/interfaces/select';
import { enumAsSelectOptions, obter_erro_request } from '@utils';
import { corrigeDuplicacaoNome } from '@home/submodulos/dados-meteorologicos/utils';
import { ToastrService } from 'ngx-toastr';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TiposForm } from '@core/enums/tipoForm';
import { DefaultOption } from '@shared/interfaces/forms';
import { format, isBefore } from 'date-fns';
import { forkJoin, map } from 'rxjs';
import { MensagemEnvioDadosResponse } from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/interfaces/monitoramento-estacoes';
import { ModalConfirmacaoEmailComponent } from '@home/submodulos/dados-meteorologicos/componentes/modal-confimacao-email/modal-confirmacao-email/modal-confirmacao-email.component';
import moment from 'moment';
import { Mensagens } from '@core/enums/mensagens';
import {
	PostoMap,
	PostoTipo,
} from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/pages/envio-de-dados-massivos-meteorologicos/types/posto-map.type';
import { TransferListComponent } from '@componentes/transfer-list/transfer-list.component';
import { TextoInformativo } from '@utils/texto-informativo';
import { EnvioDadosMassivosMeteorologicos } from '@modulos/home/submodulos/dados-meteorologicos/interfaces/envio-dados-massivo';
import { DadosMassivosService } from '@services/dados-massivos/dados-massivos.service';

@Component({
	selector: 'seira-envio-dados-massivos-meteorologicos',
	templateUrl: './envio-dados-massivos-meteorologicos.component.html',
	styleUrls: ['./envio-dados-massivos-meteorologicos.component.scss'],
})
export class EnvioDadosMassivosMeteorologicosComponent implements OnInit {
	form!: FormGroup;
	categorias: Select[] = [];
	tipos: Select[] = [];
	tipoModo = 'PCO';
	variaveis: Select[] = [];

	@ViewChild('transferListComponent')
	transferListComponent!: TransferListComponent<any>;

	mapPostos: PostoMap<EnvioDadosMassivosMeteorologicosComponent> = {
		PCO: {
			disponiveis: 'postosPco',
			selecionados: 'postosPcoSelecionados',
		},
		SUDENE: {
			disponiveis: 'postosSudene',
			selecionados: 'postosSudeneSelecionados',
		},
		PCD: {
			disponiveis: 'postosPcd',
			selecionados: 'postosPcdSelecionados',
		},
	};

	postosPco: DefaultOption[] = [];
	postosPcoSelecionados: DefaultOption[] = [];

	postosPcd: DefaultOption[] = [];
	postosPcdSelecionados: DefaultOption[] = [];

	postosSudene: DefaultOption[] = [];
	postosSudeneSelecionados: DefaultOption[] = [];

	sudenePeriodo: { min: Date; max: Date } | null = null;
	private periodoCachePorPosto = new Map<
		number,
		{ primeira: string; ultima: string } | null
	>();

	postosDisponiveis: DefaultOption[] = [];
	postosSelecionados: DefaultOption[] = [];

	tipoAtual = '';
	reiniciando = false;

	hoje = new Date();

	opcoesModo: OptionRadio<'PCO' | 'PCD' | 'SUDENE'>[] = [
		{ label: 'PCO', value: 'PCO' },
		{ label: 'PCD', value: 'PCD' },
		{ label: 'SUDENE', value: 'SUDENE' },
	];

	opcoesPeriodoCompleto: OptionRadio<PeriodosLabel>[] = [
		{ label: 'Horário', value: 'horario' },
		{ label: 'Diário', value: 'diario' },
		{ label: 'Mensal', value: 'mensal' },
		{ label: 'Anual parcial', value: 'anual_parcial' },
		{ label: 'Anual completo', value: 'anual' },
	];

	datePickerType: 'day' | 'month' | 'year' = 'day';

	opcoesPeriodo: OptionRadio<PeriodosLabel>[] =
		this.opcoesPeriodoCompleto.filter(op => op.value !== 'horario');

	informacoesGerais: TextoInformativo = {
		label: Mensagens.INFORMACOES_GERAIS,
		texts: [
			{
				title: 'O que você vai encontrar nesta página?',
				text:
					'Acesso a informações meteorológicas, ' +
					'importantes para a gestão de recursos <br> ' +
					'hídricos, agricultura, ' +
					'defesa civil e pesquisa científica. <br><br> ' +
					'<strong>Tipos de estação: </strong><br>' +
					'PCO: Pluviômetro Tipo <i class="fst-italic fw-medium">Ville de Paris</i> com medição manual; <br>' +
					'PCD: Plataforma de Coleta de Dados.',
			},
			{
				title: 'Como funciona?',
				text:
					'Esta página permite que o usuário solicite todo o histórico de dados da AESA. <br>' +
					'Após preencher o formulário e clicar em "Enviar solicitação",<br>' +
					'um link para download será enviado por e-mail e ficará disponível por 48 horas.',
			},
		],
	};

	protected readonly FormularioRelatorio = FormularioRelatorio;
	protected readonly TiposForm = TiposForm;

	constructor(
		private formBuilder: FormBuilder,
		private modalService: BsModalService,
		readonly relatoriosService: RelatoriosService,
		private readonly dadosMassivosService: DadosMassivosService,
		private toastr: ToastrService
	) {}

	ngOnInit(): void {
		this.variaveis = enumAsSelectOptions({ PRECIPITACAO: 'Precipitação' });
		this.setFormControls();
		this.buscarFiltrosEstacoes();
		this.observarTipoEstacao();
		this.observarPeriodo();
		this.observarSelecaoSudene();
	}

	private setFormControls(): void {
		this.form = this.formBuilder.group({
			[FormularioRelatorio.TIPO_MODO]: new FormControl(
				'PCO',
				Validators.required
			),
			[FormularioRelatorio.ESTACAO]: new FormControl([], Validators.required),
			[FormularioRelatorio.VARIAVEL]: new FormControl(
				['PRECIPITACAO'],
				Validators.required
			),
			[FormularioRelatorio.PERIODO_BUSCA]: new FormControl(
				'diario',
				Validators.required
			),
			[FormularioRelatorio.DATA_INICIO]: new FormControl(
				null,
				Validators.required
			),
			[FormularioRelatorio.DATA_FIM]: new FormControl(null, [
				Validators.required,
			]),
			[FormularioRelatorio.NOME]: new FormControl(null, Validators.required),
			[FormularioRelatorio.EMAIL]: new FormControl(null, [
				Validators.required,
				Validators.email,
			]),
			[FormularioRelatorio.LOADING_GERAR]: new FormControl(false),
		});
	}

	private buscarFiltrosEstacoes() {
		this.setLoadingForm(true);

		this.relatoriosService.consultarOpcoesFiltros().subscribe({
			next: resp => {
				this.postosSudene = resp.postosSudene.map(val => {
					const nomePosto = val.nome.replace(/[.,]/g, '');
					return {
						label: nomePosto + ` (${val.codigo})`,
						value: val.id.toString(),
					};
				});

				if (resp.estacoes) {
					const estacoesFiltradasPco = resp.estacoes.filter(
						e =>
							e.tipoEstacao === 'PLUVIOMETRO_CONVENCIONAL' &&
							e.statusEstacao === 'ATIVA'
					);

					const estacoesFiltradasPcd = resp.estacoes.filter(
						e => e.tipoEstacao === 'PCD' && e.statusEstacao === 'ATIVA'
					);

					this.postosPco = estacoesFiltradasPco.map(e => {
						return {
							label: corrigeDuplicacaoNome(`${e.nomeMunicipio}/${e.nomePosto}`),
							value: e.id.toString(),
						};
					});

					this.postosPcd = estacoesFiltradasPcd.map(e => {
						return {
							label: corrigeDuplicacaoNome(`${e.nomeMunicipio}/${e.nomePosto}`),
							value: e.id.toString(),
						};
					});
				}
				this.setLoadingForm(false);

				this.atualizarPostosPorTipo('PCO');
				this.preencherValores();
			},
			error: err => {
				this.setLoadingForm(false);
				const msg_erro = obter_erro_request(err);
				this.toastr.error(
					msg_erro,
					'Erro ao buscar as informações das estações'
				);
			},
		});
	}

	atualizarPostosPorTipo(tipo: PostoTipo): void {
		const atual = this.mapPostos[this.tipoAtual as PostoTipo];
		const novo = this.mapPostos[tipo];

		if (this.tipoAtual && this.tipoAtual !== tipo && !this.reiniciando) {
			(this[atual.disponiveis] as DefaultOption[]) = this.postosDisponiveis;
			(this[atual.selecionados] as DefaultOption[]) = this.postosSelecionados;
		}

		const novosDisponiveis = this[novo.disponiveis] as DefaultOption[];
		const novosSelecionados = this[novo.selecionados] as DefaultOption[];

		this.postosDisponiveis = novosDisponiveis.filter(
			disp => !novosSelecionados.some(sel => sel.value === disp.value)
		);

		this.postosSelecionados = novosSelecionados;

		this.form.get('estacao')?.setValue(this.postosSelecionados);

		this.tipoAtual = tipo;
	}

	private preencherValores() {
		if (!this.form.get(FormularioRelatorio.VARIAVEL)?.value) {
			this.form
				.get(FormularioRelatorio.VARIAVEL)
				?.setValue(this.variaveis[0].value);
		}
	}

	observarTipoEstacao() {
		this.form.get('tipoModo')?.valueChanges.subscribe(tipo => {
			this.tipoModo = tipo;
			this.atualizarCampos(tipo);
			this.atualizarPostosPorTipo(tipo);
			this.aplicarDatasPadraoSudene();
		});
	}

	private aplicarDatasPadraoSudene() {
		if (this.tipoModo === 'SUDENE') {
			this.form
				.get(FormularioRelatorio.DATA_INICIO)
				?.setValue(new Date(1910, 0, 1));
			this.form
				.get(FormularioRelatorio.DATA_FIM)
				?.setValue(new Date(1910, 11, 31));
		} else {
			this.form.get(FormularioRelatorio.DATA_INICIO)?.setValue(null);
			this.form.get(FormularioRelatorio.DATA_FIM)?.setValue(null);
		}
	}

	observarPeriodo() {
		this.form.get('periodoBusca')?.valueChanges.subscribe(periodo => {
			this.atualizarTipoDatePicker(periodo);
		});
	}

	atualizarTipoDatePicker(periodo: string) {
		switch (periodo) {
			case 'horario':
			case 'diario':
				this.datePickerType = 'day';
				break;
			case 'mensal':
				this.datePickerType = 'month';
				break;
			case 'anual':
				this.datePickerType = 'year';
				break;
			case 'anual_parcial':
				this.datePickerType = 'month';
				break;
			default:
				this.datePickerType = 'day';
		}
	}

	private observarSelecaoSudene() {
		this.form
			.get(FormularioRelatorio.ESTACAO)
			?.valueChanges.subscribe(() => this.atualizarPeriodoSudene());
	}

	private atualizarPeriodoSudene() {
		if (this.tipoModo !== 'SUDENE') {
			this.sudenePeriodo = null;
			return;
		}
		const ids = this.postosSelecionados
			.map(posto => Number(posto.value))
			.filter(id => !Number.isNaN(id));
		if (ids.length === 0) {
			this.sudenePeriodo = null;
			return;
		}
		const idsSemCache = ids.filter(id => !this.periodoCachePorPosto.has(id));
		if (idsSemCache.length === 0) {
			this.recalcularUniaoSudene(ids);
			return;
		}
		forkJoin(
			idsSemCache.map(id =>
				this.relatoriosService
					.buscarPeriodoPostoSudene(id)
					.pipe(map(periodo => ({ id, periodo })))
			)
		).subscribe({
			next: resultados => {
				resultados.forEach(({ id, periodo }) =>
					this.periodoCachePorPosto.set(id, periodo)
				);
				this.recalcularUniaoSudene(ids);
			},
			error: () => {
				this.sudenePeriodo = null;
			},
		});
	}

	private recalcularUniaoSudene(ids: number[]) {
		const periodos = ids
			.map(id => this.periodoCachePorPosto.get(id))
			.filter(
				(periodo): periodo is { primeira: string; ultima: string } => !!periodo
			);
		if (periodos.length === 0) {
			this.sudenePeriodo = null;
			return;
		}
		const min = periodos
			.map(periodo => moment(periodo.primeira).startOf('day'))
			.reduce((a, b) => (a.isBefore(b) ? a : b))
			.toDate();
		const max = periodos
			.map(periodo => moment(periodo.ultima).endOf('day'))
			.reduce((a, b) => (a.isAfter(b) ? a : b))
			.toDate();
		this.sudenePeriodo = { min, max };
		this.limparDatasForaDoPeriodoSudene();
	}

	private limparDatasForaDoPeriodoSudene() {
		if (!this.sudenePeriodo) {
			return;
		}
		const { min, max } = this.sudenePeriodo;
		const inicio = this.getFormItemValue(FormularioRelatorio.DATA_INICIO);
		const fim = this.getFormItemValue(FormularioRelatorio.DATA_FIM);
		if (inicio) {
			const dataInicio = new Date(inicio);
			if (dataInicio < min || dataInicio > max) {
				this.form.get(FormularioRelatorio.DATA_INICIO)?.setValue(null);
			}
		}
		if (fim) {
			const dataFim = new Date(fim);
			if (dataFim < min || dataFim > max) {
				this.form.get(FormularioRelatorio.DATA_FIM)?.setValue(null);
			}
		}
	}

	get dataMinimaSudene(): Date | null {
		return this.tipoModo === 'SUDENE' ? this.sudenePeriodo?.min ?? null : null;
	}

	get dataMaximaPicker(): Date {
		if (this.tipoModo === 'SUDENE' && this.sudenePeriodo) {
			return this.sudenePeriodo.max;
		}
		return this.hoje;
	}

	get dataFimMinima(): Date | null {
		return this.dataInicio ?? this.dataMinimaSudene;
	}

	atualizarCampos(estacao: string) {
		const opcaoAnual = this.opcoesPeriodo.find(el => el.value === 'anual')!;
		if (estacao === 'PCD') {
			this.variaveis = enumAsSelectOptions(Variavel);
			this.opcoesPeriodo = this.opcoesPeriodoCompleto.filter(
				op => !['anual_parcial'].includes(op.value)
			);
			opcaoAnual.label = 'Anual';
		} else {
			this.variaveis = enumAsSelectOptions({ PRECIPITACAO: 'Precipitação' });
			this.form.get('variavel')?.setValue(['PRECIPITACAO']);
			this.opcoesPeriodo = this.opcoesPeriodoCompleto.filter(
				op => op.value !== 'horario'
			);
			opcaoAnual.label = 'Anual completo';
		}
		this.form.get('periodoBusca')?.setValue('diario');
	}

	get loadingForm() {
		return !!this.form.get(FormularioRelatorio.LOADING_GERAR)?.value;
	}

	get dataInicio() {
		const data = this.getFormItemValue(FormularioRelatorio.DATA_INICIO);

		if (data && this.datePickerType) {
			const dataObj = moment(data);

			if (this.datePickerType === 'day') {
				return dataObj.startOf('day').toDate();
			} else if (this.datePickerType === 'month') {
				return dataObj.startOf('month').toDate();
			} else if (this.datePickerType === 'year') {
				return dataObj.startOf('year').toDate();
			}
		}
		return null;
	}

	get dataFim() {
		const data = this.getFormItemValue(FormularioRelatorio.DATA_FIM);

		if (data && this.datePickerType) {
			let dataObj = moment(data);
			const hoje = moment();

			if (dataObj.isAfter(hoje)) {
				dataObj = hoje.clone();
			}

			if (this.datePickerType === 'day') {
				if (
					dataObj.date() !== hoje.date() ||
					dataObj.month() !== hoje.month() ||
					dataObj.year() !== hoje.year()
				) {
					return dataObj.endOf('day').toDate();
				}
				return dataObj.toDate();
			} else if (this.datePickerType === 'month') {
				if (
					dataObj.month() !== hoje.month() ||
					dataObj.year() !== hoje.year()
				) {
					return dataObj.endOf('month').toDate();
				}
				return dataObj.toDate();
			} else if (this.datePickerType === 'year') {
				if (dataObj.year() !== hoje.year()) {
					return dataObj.endOf('year').toDate();
				} else {
					return dataObj.toDate();
				}
			}
		}
		return null;
	}

	private setLoadingForm(valor: boolean) {
		this.form.get(FormularioRelatorio.LOADING_GERAR)?.setValue(valor);
	}

	getFormItemValue(field: string) {
		return this.form.get(field)?.value;
	}

	get emailControl() {
		return this.form.get('email');
	}

	conditionShowTextRequired(field: string): boolean {
		const control = this.form.get(field);
		return !!(control && control.invalid && (control.touched || control.dirty));
	}

	abrirModalConfirmacaoEmail() {
		if (
			this.dataInicio &&
			this.dataFim &&
			isBefore(this.dataFim, this.dataInicio)
		) {
			this.toastr.info('A data final não pode ser anterior a data inicial.');
			return;
		}

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			this.toastr.warning('Preencha todos os campos obrigatórios.');
			return;
		}

		const modalRef = this.modalService.show(ModalConfirmacaoEmailComponent, {
			initialState: {
				email: this.getFormItemValue('email'),
			},
		});

		(modalRef.content as ModalConfirmacaoEmailComponent).onClose.subscribe(
			(confirmado: boolean) => {
				if (confirmado) {
					this.solicitarDados();
				}
			}
		);
	}

	solicitarDados() {
		this.setLoadingForm(true);
		this.form.get(FormularioRelatorio.NOME)?.disable();
		this.form.get(FormularioRelatorio.EMAIL)?.disable();
		this.dadosMassivosService
			.solicitarEnvioDadosMassivosMeteorologicos(this.payloadRequisicao)
			.subscribe({
				next: (res: MensagemEnvioDadosResponse) => {
					this.setLoadingForm(false);
					this.toastr.success(res?.mensagem, 'Solicitação confirmada');
					this.resetarFormulario();
					this.form.get(FormularioRelatorio.NOME)?.enable();
					this.form.get(FormularioRelatorio.EMAIL)?.enable();
				},
				error: err => {
					this.setLoadingForm(false);
					const msg_erro = obter_erro_request(err);
					this.toastr.error(msg_erro, 'Erro ao solicitar envio de dados');
					this.form.get(FormularioRelatorio.NOME)?.enable();
					this.form.get(FormularioRelatorio.EMAIL)?.enable();
				},
			});
	}

	private get payloadRequisicao(): EnvioDadosMassivosMeteorologicos {
		const mapEquivalenciaPeriodoPCD = {
			horario: 'horario',
			diario: 'diario',
			mensal: 'mensal',
      anual:'anual'
		};
		const periodoBusca = this.getFormItemValue('periodoBusca') as string;
		return {
			nome: this.getFormItemValue(FormularioRelatorio.NOME),
			email: this.getFormItemValue(FormularioRelatorio.EMAIL),
			tipoEstacao: this.tipoModo,
			periodo:
				this.tipoModo === 'PCD'
					? mapEquivalenciaPeriodoPCD[
							periodoBusca as keyof typeof mapEquivalenciaPeriodoPCD
					  ]
					: periodoBusca,
			variaveis: this.getFormItemValue('variavel'),
			estacoes: this.postosSelecionados.map((e: any) => Number(e.value)),
			dataInicio: format(this.dataInicio!, 'yyyy-MM-dd'),
			dataFim: format(this.dataFim!, 'yyyy-MM-dd'),
		};
	}

	private resetarFormulario() {
		this.reiniciando = true;
		this.postosSelecionados = [];

		this.postosSudene = this.reintegrarPostos(
			this.postosSudene,
			this.postosSudeneSelecionados
		);
		this.postosSudeneSelecionados = [];

		this.postosPcd = this.reintegrarPostos(
			this.postosPcd,
			this.postosPcdSelecionados
		);
		this.postosPcdSelecionados = [];

		this.postosPco = this.reintegrarPostos(
			this.postosPco,
			this.postosPcoSelecionados
		);
		this.postosPcoSelecionados = [];

		this.form.reset({
			[FormularioRelatorio.TIPO_MODO]: 'PCO',
			[FormularioRelatorio.ESTACAO]: [],
			[FormularioRelatorio.VARIAVEL]: ['PRECIPITACAO'],
			[FormularioRelatorio.PERIODO_BUSCA]: 'diario',
			[FormularioRelatorio.DATA_INICIO]: null,
			[FormularioRelatorio.DATA_FIM]: null,
			[FormularioRelatorio.NOME]: null,
			[FormularioRelatorio.EMAIL]: null,
			[FormularioRelatorio.LOADING_GERAR]: false,
		});

		this.postosDisponiveis = [...this.postosPco];

		Object.keys(this.form.controls).forEach(key => {
			const control = this.form.get(key);
			control?.markAsPristine();
			control?.markAsUntouched();
			control?.updateValueAndValidity();
		});

		this.transferListComponent.resetInterno();
		this.reiniciando = false;
	}

	private reintegrarPostos(
		listaPrincipal: any[],
		listaSelecionados: any[]
	): any[] {
		const novosPostos = listaSelecionados.filter(
			p => !listaPrincipal.some(s => s.value === p.value)
		);
		return [...listaPrincipal, ...novosPostos];
	}
}
